import { useEffect, useRef, useState } from 'react'

// A text filter bound directly to a URL-backed filter value round-trips through the URL
// (qs <-> URLSearchParams) on every keystroke. That round trip can land a render late, which
// resets the input's cursor to the end if the stale value is read back into it. A local draft,
// seeded once from the current value, is the input's sole source of truth while the user types.
// A value that did not come from the user's own typing (Clear Filters, browser back/forward)
// is copied into the draft, so the input never disagrees with the filter state.
export const useSearchDraft = (
  value: string | undefined,
  onChange: (value: string) => void,
): [string, (value: string) => void] => {
  const [draft, setDraft] = useState(value ?? '')
  // Note: values the user typed that the filter state has not echoed back yet, oldest first.
  const pendingEchoes = useRef<string[]>([])

  useEffect(() => {
    const incoming = value ?? ''
    const echoIndex = pendingEchoes.current.indexOf(incoming)

    if (echoIndex !== -1) {
      pendingEchoes.current = pendingEchoes.current.slice(echoIndex + 1)
      return
    }

    pendingEchoes.current = []
    setDraft(incoming)
  }, [value])

  const handleChange = (next: string) => {
    pendingEchoes.current.push(next)
    setDraft(next)
    onChange(next)
  }

  return [draft, handleChange]
}
